import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, FormControl, InputLabel,
  Select, MenuItem, Alert, Tabs, Tab, Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utills/axiosInstance';
import moment from 'moment';

const AllEmployeeAttendance = () => {
  const [reportType, setReportType] = useState('week');
  const [allReportData, setAllReportData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
  });
  const [year, setYear] = useState(moment().year());
  const [month, setMonth] = useState(moment().month());
  const [selectedDate, setSelectedDate] = useState(moment().toDate());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllAttendance();
  }, [reportType, year, month, selectedDate]);

  const fetchAllAttendance = async () => {
    setLoading(true);
    setError('');
    try {
      let endpoint = '';

      if (reportType === 'day') {
        const dateStr = moment(selectedDate).format('YYYY-MM-DD');
        endpoint = `/api/reports/day/${dateStr}`;
      } else if (reportType === 'week') {
        const weekNum = moment(selectedDate).isoWeek();
        endpoint = `/api/reports/week/${year}/${weekNum}`;
      } else if (reportType === 'month') {
        endpoint = `/api/reports/month/${year}/${month+1}`;
      }

      const res = await axiosInstance.get(endpoint);
      const data = res.data.report || [];
      setAllReportData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
      setAllReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const paginatedData = useMemo(() => {
    const start = pagination.page * pagination.rowsPerPage;
    const end = start + pagination.rowsPerPage;
    return allReportData.slice(start, end);
  }, [allReportData, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    setDisplayData(paginatedData);
  }, [paginatedData]);

  const handleChangePage = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    });
  };

  const handleReportTypeChange = (event, newValue) => {
    setReportType(newValue);
    setPagination({ page: 0, rowsPerPage: 10 });
  };

  const formatHoursToHM = (hours) => {
    if (!hours || hours === 0) return '00:00';
    const h = Math.floor(hours);
    const m = Math.round((hours % 1) * 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>Loading report...</div>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ p: 4, m: 2, borderRadius: 3, boxShadow: 4 }}>
        <Typography variant="h5" textAlign="center" mb={4}>
          All Employee Attendance Report
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tabs value={reportType} onChange={handleReportTypeChange}>
            <Tab label="Day Report" value="day" />
            <Tab label="Week Report" value="week" />
            <Tab label="Month Report" value="month" />
          </Tabs>

          {reportType === 'day' && (
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={setSelectedDate}
              slotProps={{ textField: { size: 'small', sx: { width: 220 } } }}
            />
          )}

          {reportType === 'week' && (
            <>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Year</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Year">
                  {Array.from({ length: 6 }, (_, i) => moment().year() - i).map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                label="Any Date in Week"
                value={selectedDate}
                onChange={setSelectedDate}
                slotProps={{ textField: { size: 'small', sx: { width: 240 } } }}
              />
            </>
          )}

          {reportType === 'month' && (
            <>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Year</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Year">
                  {Array.from({ length: 6 }, (_, i) => moment().year() - i).map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Month</InputLabel>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Month">
                  {moment.months().map((m, i) => (
                    <MenuItem key={i} value={i}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>

        <Typography variant="h6" color="text.primary" gutterBottom>
          {reportType === 'day'
            ? moment(selectedDate).format('dddd, MMMM DD, YYYY')
            : reportType === 'week'
            ? `Week: ${moment(selectedDate).startOf('isoWeek').format('MMM DD')} – ${moment(selectedDate).endOf('isoWeek').format('MMM DD, YYYY')}`
            : `${moment().year(year).month(month).format('MMMM YYYY')}`}
        </Typography>

        <TableContainer sx={{ maxHeight: 620, border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Employee Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Email</TableCell>

                {reportType === 'day' && (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Leave Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Check In</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Check Out</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Total Hours</TableCell>
                  </>
                )}
                
                {(reportType === 'week' || reportType === 'month') && (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Total Working Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Present Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Absent Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Leave Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>OD Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '15px' }}>Total Hours</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {displayData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No attendance records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayData.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{row.employee.firstName} {row.employee.lastName}</TableCell>
                    <TableCell>{row.employee.email}</TableCell>

                    {reportType === 'day' && (
                      <>
                        <TableCell>{row.status || '—'}</TableCell>
                        <TableCell>{row.leaveType || 'N/A'}</TableCell>
                        <TableCell>{row.checkin ? moment(row.checkin).format('HH:mm') : '—'}</TableCell>
                        <TableCell>{row.checkout ? moment(row.checkout).format('HH:mm') : '—'}</TableCell>
                        <TableCell>{formatHoursToHM(row.totalHours)}</TableCell>
                      </>
                    )}

                    {(reportType === 'week' || reportType === 'month') && (
                      <>
                        <TableCell>{row.totalWorkingDays || row.totalDays || 0}</TableCell>
                        <TableCell>{row.presentDays || 0}</TableCell>
                        <TableCell>{row.absentDays || 0}</TableCell>
                        <TableCell>{row.leaveDays || 0}</TableCell>
                        <TableCell>{row.odDays || 0}</TableCell>
                        <TableCell>{formatHoursToHM(row.totalHours)}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={allReportData.length}
          page={pagination.page}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="Rows per page:"
          sx={{ mt: 2 }}
        />
      </Paper>
    </LocalizationProvider>
  );
};

export default AllEmployeeAttendance;
